import React, { useEffect, useState } from 'react';
import { Alert, Card, Grid, Stack, TextField } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { Form, FormikProvider, useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { When } from 'react-if';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';

import { OnboardingService } from '../../../services/onboarding.service';
import { OnboardingTypes } from '../../../types/onboarding.types';
import Loading from '../../../components/loading';
import { CustomerTypes } from "../../../types";
import { RootState } from '../../../store';
import UserFirstLoginLayout from '../../auth/user-first-login/layout/user-first-login-layout';
import * as S from './styles';
import { BackButton } from '../../../components/back-button';
import { CustomerService } from '../../../services/customer.service';
import { onboardingStepperValidationForm } from '../../../schemas/onboarding.schema';
import { GT_COLORS } from '../../../constants/designTokens.constants';
import { RESPONSE_CODES } from '../../../constants/responseCodes.constants';

const TOTAL_STEPS = 2;

const STEP_COPY: Array<{ title: string; subtitle: string }> = [
  {
    title: 'Welcome to POM Aware',
    subtitle: 'Let’s start by confirming your organization name.',
  },
  {
    title: 'Who should we list as your primary contact?',
    subtitle: 'This is the name we’ll show on your POM admin account. To finish, enter the temporary password from your invitation email.',
  },
];

export const CustomerRegistrationOnboardingScreen: React.FC = () => {
  const navigate = useNavigate();
  const [actualStep, setActualStep] = useState<number>(0);
  const { loading } = useSelector((state: RootState) => state.customer);

  const [state, setState] = useState<{
    fetching: boolean;
    details: OnboardingTypes.InviteInfo;
    err: undefined | string;
  }>({
    fetching: true,
    details: {} as OnboardingTypes.InviteInfo,
    err: undefined,
  });

  const { uuid } = useParams();

  const formik = useFormik<CustomerTypes.OnboardingForm>({
    initialValues: {} as CustomerTypes.OnboardingForm,
    validationSchema: onboardingStepperValidationForm[actualStep],
    onSubmit: async (values, helpers) => {
      // Trim here, not just in the schema: Yup transforms are validation-only
      // under Formik, so the submitted value would otherwise keep the padding
      // that email-client copy-paste adds.
      await CustomerService.finishOnboarding({
        ...values,
        tempPassword: values.tempPassword?.trim(),
        invitationId: uuid!,
      }, (isValid, msg) => {
        // A wrong temp password also marks the field itself: the toast
        // auto-dismisses, and the user needs a persistent cue while they dig
        // the password out of their email.
        if (!isValid && msg === RESPONSE_CODES.INVALID_TEMP_PASSWORD) {
          helpers.setFieldError('tempPassword', msg);
        }
        toast(msg, { autoClose: isValid ? 1500 : 6000, type: isValid ? 'success' : 'error' });
        if (isValid) {
          navigate('/');
        }
      });
    },
  });

  const { values, validateForm, touched, errors, getFieldProps } = formik;

  const handleNextStep = async () => {
    const result = await validateForm(values);
    if (Object.keys(result).length > 0) return;

    if (actualStep === TOTAL_STEPS - 1) {
      await formik.submitForm();
      return;
    }
    setActualStep(step => step + 1);
  };

  const handlePreviousStep = () => setActualStep(step => step - 1);

  const shouldRenderSteps = !state.err && !state.fetching;
  const stepCopy = STEP_COPY[actualStep];

  useEffect(() => {
    if (!uuid) return;
    OnboardingService.getCustomerProcessByUUID(uuid, (isValid, message, result) => {
      if (!isValid) {
        setState((s) => ({ ...s, fetching: false, err: message }));
        return;
      }
      setState((s) => ({ ...s, fetching: false }));
      formik.setFieldValue('companyName', result?.customer?.name || '');
      formik.setFieldValue('contactFirstName', result?.customer?.contactFirstName || '');
      formik.setFieldValue('contactLastName', result?.customer?.contactLastName || '');
      formik.setFieldValue('tempPassword', '');
    });
  }, [uuid]);

  return (
    <UserFirstLoginLayout>
      <S.Container>
        <When condition={state?.fetching}>
          <Loading />
        </When>

        <When condition={!!state.err}>
          <Alert variant="filled" severity="error" sx={{ borderRadius: '10px' }}>
            Oops! We can't seem to find your invitation.
            <br />
            {state.err}
          </Alert>
          <Card
            sx={{
              mt: 2,
              p: { xs: 2, md: 3 },
              border: `1px solid ${GT_COLORS.border}`,
              borderRadius: '12px',
              boxShadow: 'none',
            }}
          >
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Already have your account details? If your invitation email included a
                temporary password, you can sign in with it directly and you’ll be asked
                to create your own password.
              </Typography>
              <LoadingButton variant="contained" size="large" onClick={() => navigate('/login')}>
                Go to login
              </LoadingButton>
            </Stack>
          </Card>
          <img style={{ marginTop: 20 }} src={'/static/illustrations/invite_not_found_2.svg'} />
        </When>

        <When condition={shouldRenderSteps}>
          <Stack spacing={3} sx={{ width: '100%', mt: 2 }}>
            <When condition={actualStep > 0}>
              <BackButton onClick={handlePreviousStep} />
            </When>

            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Step {actualStep + 1} of {TOTAL_STEPS}
              </Typography>
              <Typography variant="h4" color="text.primary">
                {stepCopy.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {stepCopy.subtitle}
              </Typography>
            </Stack>

            <Card
              sx={{
                p: { xs: 2, md: 4 },
                border: `1px solid ${GT_COLORS.border}`,
                borderRadius: '12px',
                boxShadow: 'none',
              }}
            >
              <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate>
                  <When condition={actualStep === 0}>
                    <Stack spacing={3}>
                      <TextField
                        size="medium"
                        label="Organization name"
                        placeholder="Acme Health"
                        fullWidth
                        {...getFieldProps('companyName')}
                        error={Boolean(touched.companyName && errors.companyName)}
                        helperText={touched.companyName && errors.companyName}
                      />
                    </Stack>
                  </When>

                  <When condition={actualStep === 1}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="First name"
                          placeholder="First"
                          {...getFieldProps('contactFirstName')}
                          error={Boolean(touched.contactFirstName && errors.contactFirstName)}
                          helperText={touched.contactFirstName && errors.contactFirstName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Last name"
                          placeholder="Last"
                          {...getFieldProps('contactLastName')}
                          error={Boolean(touched.contactLastName && errors.contactLastName)}
                          helperText={touched.contactLastName && errors.contactLastName}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="medium"
                          type="password"
                          label="Temporary password"
                          placeholder="From your invitation email"
                          {...getFieldProps('tempPassword')}
                          error={Boolean(touched.tempPassword && errors.tempPassword)}
                          helperText={
                            (touched.tempPassword && errors.tempPassword) ||
                            'You’ll find this in the email that brought you here.'
                          }
                        />
                      </Grid>
                    </Grid>
                  </When>

                  <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
                    <LoadingButton
                      variant="contained"
                      size="large"
                      onClick={handleNextStep}
                      loading={loading.finishOnboarding}
                      disabled={
                        loading.finishOnboarding ||
                        (actualStep === 0 && !values.companyName) ||
                        (actualStep === 1 && (!values.contactFirstName || !values.contactLastName || !values.tempPassword?.trim()))
                      }
                    >
                      {actualStep === TOTAL_STEPS - 1 ? 'Submit' : 'Next'}
                    </LoadingButton>
                  </Stack>
                </Form>
              </FormikProvider>
            </Card>
          </Stack>
        </When>
      </S.Container>
    </UserFirstLoginLayout>
  );
};
